import React, { useMemo, useState } from "react";
import { Location } from "@/types";
import { useDaysArray } from "@/hooks/useDaysArray";
import { format } from "date-fns";
import DayButton from "@tripbuilder/cat-schedule/DayButton";

interface DayNavigationProps {
  selectedLocation: Location;
  selectedDay: string;
  handleSelect: (date: string) => void;
  containerRef: React.RefObject<HTMLDivElement>;
  buttonsWrapperRef: React.RefObject<HTMLDivElement>;
  offsetX: number;
  maxOffsetX: number;
  containerWidth: number;
  isOverflowing: boolean;
  onNext: () => void;
  onPrev: () => void;
}

// Memoize DayNavigation component
const DayNavigation = React.memo<DayNavigationProps>(
  ({
    selectedLocation,
    selectedDay,
    handleSelect,
    containerRef,
    buttonsWrapperRef,
    offsetX,
    maxOffsetX,
    containerWidth,
    isOverflowing,
    onNext,
    onPrev,
  }) => {
    const { daysArray } = useDaysArray(
      selectedLocation.date.start_date,
      selectedLocation.date.end_date,
      selectedLocation.date.time_zone || "UTC"
    );
    const [isHovered, setIsHovered] = useState(false);

    const navigationButtons = useMemo(
      () => (
        <>
          {isOverflowing && (
            <button
              className={`absolute left-1 top-1/2 transform -translate-y-1/2 z-10 flex items-center justify-center w-7 h-7 bg-white rounded-full shadow-md transition-opacity duration-300 ${
                isHovered && offsetX > 0 ? "opacity-100" : "opacity-0"
              }`}
              onClick={onPrev}
              disabled={!isHovered || offsetX <= 0}
            >
              <i className="fas fa-chevron-left text-sm text-neutral-600"></i>
            </button>
          )}
          {isOverflowing && (
            <button
              className={`absolute right-1 top-1/2 transform -translate-y-1/2 z-10 flex items-center justify-center w-7 h-7 bg-white rounded-full shadow-md transition-opacity duration-300 ${
                isHovered && offsetX < maxOffsetX ? "opacity-100" : "opacity-0"
              }`}
              onClick={onNext}
              disabled={!isHovered || offsetX >= maxOffsetX}
            >
              <i className="fas fa-chevron-right text-sm text-neutral-600"></i>
            </button>
          )}
        </>
      ),
      [isHovered, isOverflowing, offsetX, maxOffsetX, onNext, onPrev]
    );

    return (
      <div className="w-full border-[1.5px] border-neutral-200/75 rounded-[10px] p-1.5 2xl:p-2">
        <div
          className="relative w-full overflow-hidden duration-300 rounded-md ease-in-out"
          ref={containerRef}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          {navigationButtons}
          <div
            className="flex flex-row space-x-2 2xl:space-x-3 transition-transforms duration-300 ease-in-out"
            style={{ transform: `translateX(-${offsetX}px)` }}
            ref={buttonsWrapperRef}
          >
            {daysArray.map((day) => (
              <DayButton
                key={format(day, "M-d")}
                date={day}
                selectedDate={selectedDay}
                handleSelect={handleSelect}
              />
            ))}
          </div>
        </div>
      </div>
    );
  }
);

DayNavigation.displayName = "DayNavigation";

export default DayNavigation;
